
import React, { useRef, useState, useEffect } from "react";
import { Box, Grid2, Typography, useTheme } from "@mui/material";

import Highcharts, { solidgauge } from "highcharts";
import more from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { useGetSchedulePerformanceQuery } from "store/slices/apis/scheduleDashboarSlice";
import { useParams } from "react-router-dom";

if (typeof more === "function") {
    more(Highcharts);
}

const SchedulePerformanceIndex = () => {
    const theme = useTheme();
    const { projectId } = useParams();

    const chartRef = useRef(null);
    const { data, isLoading } = useGetSchedulePerformanceQuery({ projectId });


    if (isLoading) {
        return (
            <Box
                sx={{
                    padding: 2,
                    pb: 0,
                    border: '1px solid #2525252E',
                    borderRadius: '7px',
                    minHeight: '240px',
                    background: "#FFFFFF",
                    width: "100%",
                    '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                        '100%': { opacity: 1 },
                    }
                }}
            >
                <Grid2 size={{ xs: 12, md: 12 }} sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: "500", fontSize: '18px', color: `${theme?.customSettings?.themeColor}`, lineHeight: '100%', letterSpacing: '-0.4px' }}>
                        Schedule Performance Indicator
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: "500", fontSize: '14px', color: `#182A4E80`, lineHeight: '100%', letterSpacing: '-0.4px' }}>
                        Get a holistic view of how work is being prioritised
                    </Typography>
                </Grid2>

                {/* Skeleton for Half Circle Gauge */}
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                    <Box
                        sx={{
                            width: "90%",
                            height: "90%",
                            borderTopLeftRadius: '160px',
                            borderTopRightRadius: '160px',
                            backgroundColor: '#E0E0E0',
                            animation: 'pulse 1.5s infinite ease-in-out'
                        }}
                    />
                </Box>
            </Box>
        );
    }

    const speed = data?.schedulePerformanceIndex || 0;
    const clampedValue = Math.min(speed, 1);

    const chartOptions = {
        chart: {
            type: "gauge",
            plotBackgroundColor: null,
            plotBackgroundImage: null,
            plotBorderWidth: 0,
            plotShadow: false,
            height: "50%",
        },
        title: {
            text: "",
        },
        pane: {
            startAngle: -90,
            endAngle: 89.9,
            background: null,
            center: ["50%", "95%"],
            size: "180%",
        },
        yAxis: {
            min: 0,
            max: 1,
            tickPixelInterval: 72,
            tickPosition: "inside",
            tickColor: Highcharts.defaultOptions.chart.backgroundColor || "#FFFFFF",
            tickLength: 20,
            tickWidth: 0,
            minorTickInterval: null,
            labels: {
                enabled: false,
                // distance: 20,
                style: {
                    fontSize: "14px",
                },
            },
            lineWidth: 0,
            plotBands: [
                {
                    from: 0.0,
                    to: 0.19,
                    color: "#E44F3F",
                    thickness: 40,
                    borderRadius: '2px'
                },
                {
                    from: 0.2,
                    to: 0.39,
                    color: "#E96E61",
                    thickness: 40,
                    borderRadius: '2px'
                },
                {
                    from: 0.4,
                    to: 0.59,
                    color: "#FECE51",
                    thickness: 40,
                    borderRadius: '2px'
                },
                {
                    from: 0.6,
                    to: 0.79,
                    color: "#9ED3CC",
                    thickness: 40,
                    borderRadius: '2px'
                },
                {
                    from: 0.8,
                    to: 1,
                    color: "#49ABA0",
                    thickness: 40,
                    borderRadius: '2px'
                },
            ],
        },
        credits: {
            enabled: false,
        },
        series: [
            {
                name: "Schedule Performance Index",
                data: [clampedValue],
                tooltip: {
                    valueSuffix: "",
                },
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    formatter: function () {
                        return `<div>${speed.toFixed(2)}</div>`;
                    },
                    borderWidth: 0,
                    y: -60,
                    // x:20,
                    style: {
                        fontSize: "30px",
                        color:
                            (Highcharts.defaultOptions.title &&
                                Highcharts.defaultOptions.title.style &&
                                Highcharts.defaultOptions.title.style.color) ||
                            "#333333",
                    },
                },
                dial: {
                    radius: "80%",
                    backgroundColor: "gray",
                    baseWidth: 10,
                    baseLength: "0%",
                    rearLength: "0%",
                },
                pivot: {
                    backgroundColor: "gray",
                    radius: 6,
                },
            },
        ],
    };

    return (
        <Box sx={{ padding: 2, pb: 0, border: '1px solid #2525252E', borderRadius: '7px', minHeight: '240px', background: "#FFFFFF", width: "100%" }}>
            <Grid2 size={{ xs: 12, md: 12 }} sx={{ mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "500", fontSize: '18px', color: `${theme?.customSettings?.themeColor}`, lineHeight: '100%', letterSpacing: '-0.4px' }}>
                    Schedule Performance Index
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: "500", fontSize: '14px', color: `#182A4E80`, lineHeight: '100%', letterSpacing: '-0.4px' }}>
                    Indicating schedule efficiency to assess adherence to planned timelines
                </Typography>
            </Grid2>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
        </Box>
    );
};

export default SchedulePerformanceIndex;
